
import React, { useState, useRef } from "react";
import { LinkInBioProfile } from "@/types/linkinbio";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/ui/enhanced-avatar";
import { Upload, X, Loader2 } from "lucide-react";
import { SupabaseStorage } from "@/lib/supabase-storage";
import { LinkInBioAPI } from "@/lib/linkinbio-api";
import { supabase } from "@/integrations/supabase/client";
import { shadcnToast } from "@/hooks/use-toast";

interface ProfileSectionProps {
  profile: LinkInBioProfile;
  onProfileUpdate: (profile: Partial<LinkInBioProfile>) => void;
}

export const ProfileSection: React.FC<ProfileSectionProps> = ({ profile, onProfileUpdate }) => {
  const [usernameAvailable, setUsernameAvailable] = useState(true);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const handleBookingUrlInfo = () => {
    shadcnToast({
      description: "To change your LinkInBio URL, update your booking URL in Business Settings",
    });
  };
  
  const handleProfileImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setIsUploadingImage(true);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      // Delete old image if exists
      if (profile.profileImage) {
        const oldPath = SupabaseStorage.getPathFromUrl(profile.profileImage);
        if (oldPath) {
          await SupabaseStorage.deleteImage(oldPath);
        }
      }
      
      // Generate new path and upload
      const imagePath = SupabaseStorage.generateProfileImagePath(user.id, 'avatar');
      const imageUrl = await SupabaseStorage.uploadImage(file, imagePath);
      
      if (imageUrl) {
        onProfileUpdate({ profileImage: imageUrl });
        shadcnToast({
          description: "Profile image uploaded successfully!"
        });
      } else {
        throw new Error('Failed to upload image');
      }
    } catch (error: any) {
      console.error('Error uploading image:', error);
      shadcnToast({
        description: error.message || "Failed to upload image",
        variant: "destructive"
      });
    } finally {
      setIsUploadingImage(false);
      // Reset the input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const handleRemoveImage = async () => {
    try {
      // Delete from storage if it's a Supabase URL
      if (profile.profileImage) {
        const imagePath = SupabaseStorage.getPathFromUrl(profile.profileImage);
        if (imagePath) {
          await SupabaseStorage.deleteImage(imagePath);
        }
      }
      
      onProfileUpdate({ profileImage: undefined });
      shadcnToast({
        description: "Profile image removed successfully!"
      });
    } catch (error) {
      console.error('Error removing image:', error);
      shadcnToast({
        description: "Failed to remove image",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h3 className="text-lg font-medium">Profile Information</h3>
        <p className="text-sm text-gray-500">
          Set up your basic profile information.
        </p>
      </div>
      
      <div className="space-y-4">
        {/* Profile Image */}
        <div className="flex flex-col items-center space-y-4">
          <div className="relative">
            <UserAvatar
              src={profile.profileImage}
              alt={profile.displayName || 'Profile'}
              value={profile.displayName || profile.username || 'user'}
              fallbackText={profile.displayName?.charAt(0) || '?'}
              className="w-24 h-24 text-2xl"
            />
            {profile.profileImage && (
              <button 
                onClick={handleRemoveImage}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                aria-label="Remove image"
              >
                <X size={14} />
              </button>
            )}
          </div>
          
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleProfileImageUpload}
            accept="image/*"
            className="hidden"
          />
          
          <Button 
            type="button" 
            variant="outline" 
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploadingImage}
          >
            {isUploadingImage ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload size={14} className="mr-2" />
                Upload Image
              </>
            )}
          </Button>
        </div>
        
        {/* LinkInBio URL (read-only, managed via business profile) */}
        <div className="space-y-2">
          <Label>LinkInBio URL</Label>
          <div className="flex items-center space-x-2">
            <Input
              value={`figgy.link/${profile.username || 'your-booking-url'}`}
              disabled
              className="bg-gray-50"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleBookingUrlInfo}
            >
              Change
            </Button>
          </div>
          <div className="mt-1 text-sm text-gray-500">
            Your LinkInBio URL is synced with your business booking URL. Change it in Business Settings.
          </div>
        </div>
        
        {/* Display Name */}
        <div className="space-y-2">
          <Label htmlFor="displayName">Display Name</Label>
          <Input
            id="displayName"
            value={profile.displayName}
            onChange={(e) => onProfileUpdate({ displayName: e.target.value })}
            placeholder="Your Name"
          />
        </div>
        
        {/* Bio */}
        <div className="space-y-2">
          <Label htmlFor="bio">Bio</Label>
          <Textarea
            id="bio"
            value={profile.bio || ""}
            onChange={(e) => onProfileUpdate({ bio: e.target.value })}
            placeholder="Write a short bio about yourself"
            rows={3}
          />
        </div>
      </div>
    </div>
  );
};
